import React from "react";
import { X, Loader2, AlertCircle } from "lucide-react";
import { MONTH_OPTIONS } from "../../constants/hrConstants";

export function GeneratePayslipModal({ 
  isOpen, onClose, employees, paySetups, formData, formErrors, 
  onFieldChange, onSubmit, isSaving, yearOptions 
}) {
  if (!isOpen) return null;

  const hasPaySetup = (empId) => paySetups.some((s) => s.employeeId === empId);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop blur */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      
      {/* Modal Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col animate-in fade-in zoom-in-95 duration-200 border border-slate-100">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div>
          <h2 className="text-[15px] font-bold text-slate-800">Generate Payslip</h2>
            <p className="text-[12px] text-slate-400 mt-0.5">Configure company and employee payroll slip for <span className="font-semibold text-slate-600">{formData.companyName || "Verve Kitchens"}</span></p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Zoho style Company Details Header */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-start border-b border-slate-100 pb-6">
            
            {/* Left side: Logo Uploader & Details fields (3/5 width) */}
            <div className="md:col-span-3 space-y-4">
              
              {/* Logo block */}
              <div className="flex gap-4 items-center">
                <div 
                  onClick={() => document.getElementById("logo-upload-input").click()}
                  className="w-20 h-20 border-2 border-dashed border-slate-300 hover:border-[#2b448f]/50 hover:bg-slate-100/50 rounded-lg flex flex-col items-center justify-center bg-slate-50 relative overflow-hidden flex-shrink-0 cursor-pointer transition-all group"
                  title="Click to Upload Company Logo"
                >
                  {formData.companyLogoUrl ? (
                    <>
                      <img src={formData.companyLogoUrl} alt="Company Logo" className="w-full h-full object-contain p-1" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-[10px] font-bold text-white">Change Logo</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-center p-2 flex flex-col items-center">
                      <svg className="w-5 h-5 text-slate-400 group-hover:text-[#2b448f] mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      <span className="text-[9px] font-bold text-slate-400 group-hover:text-[#2b448f]">Upload Logo</span>
                    </div>
                  )}
                </div>
                <input
                  id="logo-upload-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (evt) => {
                        onFieldChange("companyLogoUrl", evt.target.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                <div className="flex-1 space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Company Logo</span>
                  <p className="text-[11px] text-slate-500">Select a logo image to display on the printed payslip sheet.</p>
                  {formData.companyLogoUrl && (
                    <button 
                      type="button"
                      onClick={() => onFieldChange("companyLogoUrl", "")}
                      className="text-[10.5px] text-red-500 font-bold hover:underline"
                    >
                      Remove Logo
                    </button>
                  )}
                </div>
              </div>

              {/* Zoho style text input lines */}
              <div className="space-y-3 pt-1">
                <div>
                  <input
                    type="text" 
                    className="w-full border-b border-slate-200 focus:border-[#2b448f] outline-none text-[14px] font-bold text-slate-800 py-1 transition-colors bg-transparent placeholder-slate-400"
                    placeholder="Company Name *"
                    value={formData.companyName || ""}
                    onChange={(e) => onFieldChange("companyName", e.target.value)}
                  />
                </div>
                <div>
                  <input
                    type="text" 
                    className="w-full border-b border-slate-100 focus:border-[#2b448f] outline-none text-[12px] text-slate-600 py-0.5 transition-colors bg-transparent placeholder-slate-400"
                    placeholder="Company Address"
                    value={formData.companyAddress || ""}
                    onChange={(e) => onFieldChange("companyAddress", e.target.value)}
                  />
                </div>
                <div>
                  <input
                    type="text" 
                    className="w-full border-b border-slate-100 focus:border-[#2b448f] outline-none text-[12px] text-slate-600 py-0.5 transition-colors bg-transparent placeholder-slate-400"
                    placeholder="City, Pincode"
                    value={formData.companyCity || ""}
                    onChange={(e) => onFieldChange("companyCity", e.target.value)}
                  />
                </div>
                <div>
                  <input
                    type="text" 
                    className="w-full border-b border-slate-100 focus:border-[#2b448f] outline-none text-[12px] text-slate-600 py-0.5 transition-colors bg-transparent placeholder-slate-400"
                    placeholder="Country"
                    value={formData.companyCountry || "India"}
                    onChange={(e) => onFieldChange("companyCountry", e.target.value)}
                  />
                </div>
              </div>

            </div>

            {/* Right side: Month and Year Card (2/5 width) */}
            <div className="md:col-span-2">
              <div className="bg-slate-50/70 border border-slate-100 rounded-xl p-4 flex flex-col justify-center space-y-3">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Payslip For the Month</p>
                
                <div className="space-y-2">
                  <select
                    className={`input bg-white text-[12px] h-9 py-1 ${formErrors.month ? "border-red-400 focus:ring-red-100" : "border-slate-200"}`}
                    value={formData.month || ""}
                    onChange={(e) => onFieldChange("month", e.target.value)}
                  >
                    <option value="">Select Month</option>
                    {MONTH_OPTIONS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  {formErrors.month && <p className="text-red-500 text-[10px] mt-0.5">{formErrors.month}</p>}
                  
                  <select
                    className="input bg-white text-[12px] h-9 py-1 border-slate-200"
                    value={formData.year || ""}
                    onChange={(e) => onFieldChange("year", e.target.value)}
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

          </div>

          {/* Employee Selection Section */}
          <div className="space-y-3">
            <div>
              <label className="label text-[11px] font-bold text-slate-500 uppercase tracking-wide">Target Employee *</label>
              <select
                className={`input mt-1 bg-white text-[13px] ${formErrors.employeeId ? "border-red-400 focus:ring-red-100" : "border-slate-200"}`}
                value={formData.employeeId || ""}
                onChange={(e) => onFieldChange("employeeId", e.target.value)}
              >
                <option value="">Select Employee from directory</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name} ({e.id}) {!hasPaySetup(e.id) ? "— No pay setup found" : ""}
                  </option>
                ))}
              </select>
              {formErrors.employeeId && <p className="text-red-500 text-[11px] mt-1">{formErrors.employeeId}</p>}
            </div>

            {/* Pay setup warning */}
            {formData.employeeId && !hasPaySetup(formData.employeeId) && (
              <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5 animate-bounce" />
                <p className="text-[12px] text-amber-700 font-medium">
                  No active pay setup found for this employee. Please configure their Pay Setup first in order to generate a payslip.
                </p>
              </div>
            )}

            {/* Duplicate warning */}
            {formErrors.duplicate && (
              <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-[12px] text-red-700 font-medium">{formErrors.duplicate}</p>
              </div>
            )}
          </div>

          {/* Attendance Section */}
          <div className="border-t border-slate-100 pt-5">
            <h3 className="text-[12px] font-bold text-slate-700 uppercase tracking-wider mb-3">Attendance Days Configuration</h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                { key: "workingDays", label: "Working Days" },
                { key: "presentDays", label: "Present Days" },
                { key: "leaveDays", label: "Leave Days" },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="label text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
                  <input
                    type="number" 
                    className="input mt-1 bg-white text-[13px] border-slate-200" 
                    min="0" 
                    max="31"
                    value={formData[key] || 0}
                    onChange={(e) => onFieldChange(key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3 flex-shrink-0">
          <button onClick={onClose} className="btn-secondary" disabled={isSaving}>Cancel</button>
          <button
            onClick={onSubmit}
            className="btn-primary min-w-[130px] shadow-sm"
            disabled={isSaving || (formData.employeeId && !hasPaySetup(formData.employeeId))}
          >
            {isSaving ? (
              <span className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</span>
            ) : "Generate Payslip"}
          </button>
        </div>

      </div>
    </div>
  );
}
